import axios from 'axios'

import { downloadBlob } from 'cng-common/helpers/io'
import { getTokens } from 'core/classes/helpers/StorageHelper'
import { Rest } from 'core/classes/http'
import Logger from 'core/utils/Logger'
import settings from 'src/config/settings'

import type { ICockpitExportData, ICockpitExportPayload } from './exportCockpitTypes'

/** Characters Windows and macOS refuse in a file name. */
const UNSAFE_FILE_NAME_CHARS = /[\\/:*?"<>|]+/g

/** Enough of a refusal to identify it in the log, without dumping a body of unknown size. */
const ERROR_BODY_LIMIT = 500

/**
 * Whether the response really is the deck.
 *
 * `HttpRequest` reads this response as a blob — it picks `responseType` by URL, and
 * `/exportCockpit` matches its `/export` test — so a refusal arrives as a Blob just the same. One
 * reported with HTTP 200 and a JSON body would pass an `instanceof` check and be saved as a
 * `.pptx` full of JSON, so the payload type is what decides.
 */
const isPresentation = (blob: Blob) =>
  blob.size > 0 && !blob.type.includes('json') && !blob.type.startsWith('text/')

/** e.g. `Initiatives - FY26 Q1 + Q2 - Contoso.pptx`. */
const getFileName = ({ title, context }: ICockpitExportData) => {
  const name = [title, `${context.fiscalYear} ${context.period}`, context.scope.partnerName]
    .filter(Boolean)
    .join(' - ')

  return `${name.replace(UNSAFE_FILE_NAME_CHARS, ' ')}.pptx`
}

/**
 * Turns what an expanded cockpit tile currently shows into a PPTX and downloads it.
 *
 * One endpoint serves every tile — the payload's `type` says which deck to generate — so this is
 * the feature's single export call. Resolves to `false` when nothing was downloaded, leaving the
 * user-facing message to the button that knows which tile asked.
 */
export const exportCockpitPptx = async (payload: ICockpitExportPayload): Promise<boolean> => {
  const url = `${settings.httpsServices.partner360}/api/v0/reporting/exportCockpit`

  try {
    const blob = await Rest.post({ url, payload })

    if (!(blob instanceof Blob)) {
      return false
    }

    if (!isPresentation(blob)) {
      /** A blob response leaves `HttpRequest` nothing to read the service's own message from, so
       *  it is read here instead — this body is a short refusal, never the deck. */
      Logger.error(`Cockpit export refused: ${(await blob.text()).slice(0, ERROR_BODY_LIMIT)}`)

      return false
    }

    downloadBlob(getFileName(payload.data), blob)

    return true
  } catch (error) {
    Logger.error(error)

    return false
  }
}

/** Reads the service's own message out of a failed request. Available here and not in
 *  `exportCockpitPptx` because axios hands back the response, while `Rest` keeps it to itself. */
const readErrorBody = async (error: unknown) => {
  const body = axios.isAxiosError(error) ? error.response?.data : undefined

  if (!(body instanceof Blob)) {
    return ''
  }

  try {
    return (await body.text()).slice(0, ERROR_BODY_LIMIT)
  } catch {
    return ''
  }
}

/**
 * The same export taken past `Rest`, on plain axios.
 *
 * The payload still goes out as an object, so axios serializes exactly the body `Rest.post`
 * sends and the service-side validation is unaffected. Two things differ: `responseType: 'blob'`
 * is pinned here rather than inferred from the URL, and the error keeps its response, so a
 * refusal can be read back instead of arriving as a bare `Request failed with status code NNN`.
 */
export const exportCockpitPptxAxios = async (payload: ICockpitExportPayload): Promise<boolean> => {
  const [accessToken] = getTokens()

  try {
    const { data } = await axios.post<Blob>(
      `${settings.httpsServices.partner360}/api/v0/reporting/exportCockpit`,
      payload,
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )

    if (!isPresentation(data)) {
      Logger.error(`Cockpit export refused: ${(await data.text()).slice(0, ERROR_BODY_LIMIT)}`)

      return false
    }

    const url = URL.createObjectURL(data)
    const link = document.createElement('a')
    link.href = url
    link.download = getFileName(payload.data)
    link.click()
    URL.revokeObjectURL(url)

    return true
  } catch (error) {
    const body = await readErrorBody(error)

    Logger.error(body ? `Cockpit export failed: ${body}` : 'Cockpit export failed', error)

    return false
  }
}
